<template>
  <ion-page class="ppt-preview-page">
    <ion-header class="ion-no-border">
      <div class="toolbar flex-row align-center">
        <button class="toolbar-btn toolbar-btn--back" type="button" @click="onClose">
          <img class="nav-back" :src="getBrandImageUrl('ai_smart_reading/nav_back.png')" alt="" />
          <span>返回</span>
        </button>

        <div class="toolbar-title" :title="fileName || 'PPT 预览'">
          {{ fileName || "PPT 预览" }}
        </div>

        <div class="toolbar-meta flex-row align-center" v-if="!loading && pages.length">
          <span class="page-badge">{{ currentPageIndex + 1 }} / {{ pages.length }}</span>
        </div>
        <div class="toolbar-meta" v-else></div>
      </div>
    </ion-header>

    <ion-content class="stage-content" :scroll-y="false">
      <div v-if="loading" class="loading-container">
        <ion-spinner name="circles"></ion-spinner>
        <p>课件加载中...</p>
      </div>

      <div v-else class="stage flex-row align-center">
        <button
          class="page-side-btn page-side-btn--prev"
          type="button"
          :disabled="currentPageIndex === 0"
          @click="prevPage"
        >
          <ion-icon :icon="chevronBackOutline"></ion-icon>
        </button>

        <div ref="wrapperRef" class="slide-stage">
          <div
            class="scale-viewport"
            :style="{
              width: `${displayWidth}px`,
              height: `${displayHeight}px`,
            }"
          >
            <iframe
              ref="iframeRef"
              class="ppt-iframe"
              :srcdoc="pages[currentPageIndex]"
              :style="{
                width: `${slideWidth}px`,
                height: `${slideHeight}px`,
                transform: `scale(${scale})`,
              }"
              frameborder="0"
              scrolling="no"
              @load="onIframeLoad"
            ></iframe>

            <DrawingBoard
              ref="drawingBoardRef"
              :enabled="drawingEnabled"
              :default-color="drawingColor"
              :pen-width="6"
              :eraser-width="36"
              @clear="onDrawingClear"
              @change="onDrawingChange"
            />
          </div>
        </div>

        <button
          class="page-side-btn page-side-btn--next"
          type="button"
          :disabled="currentPageIndex === pages.length - 1"
          @click="nextPage"
        >
          <ion-icon :icon="chevronForwardOutline"></ion-icon>
        </button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonPage,
  IonHeader,
  IonContent,
  IonSpinner,
  IonIcon,
  modalController,
} from "@ionic/vue"
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons"
import DrawingBoard from "@/components/DrawingBoard.vue"
import { getBrandImageUrl } from "@/js/brandImages.ts"
import store from "@/js/store.ts"

/**
 * 静态 PPT 预览：fetch HTML 后按 .slide 拆页，支持上一页/下一页与画板批注
 * PPT 导出 SVG 原始尺寸：width="10in" height="5.625in"（96dpi → 960×540）
 */
const SLIDE_WIDTH = 960
const SLIDE_HEIGHT = 540

const IFRAME_BASE_STYLE = `
  html, body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    width: 100%;
    height: 100%;
    background: #fff;
  }
  .slide {
    margin: 0 !important;
    padding: 0 !important;
    line-height: normal;
  }
  svg.slides-canvas {
    display: block;
    width: 10in !important;
    height: 5.625in !important;
    max-width: none !important;
    max-height: none !important;
  }
`

export default {
  name: "PptPreview",
  components: {
    IonPage,
    IonHeader,
    IonContent,
    IonSpinner,
    IonIcon,
    DrawingBoard,
  },
  props: {
    fileName: {
      type: String,
      default: "PPT 预览",
    },
    fileHtml: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      chevronBackOutline,
      chevronForwardOutline,
      pages: [],
      currentPageIndex: 0,
      loading: true,
      scale: 1,
      slideWidth: SLIDE_WIDTH,
      slideHeight: SLIDE_HEIGHT,
      displayWidth: SLIDE_WIDTH,
      displayHeight: SLIDE_HEIGHT,
      resizeObserver: null,
      drawingEnabled: true,
      drawingColor: "#E53935",
      pageDrawings: {},
      headAssets: "",
    }
  },
  async mounted() {
    window.addEventListener("resize", this.handleResize)
    window.addEventListener("orientationchange", this.handleResize)
    window.addEventListener("keydown", this.handleKeydown)
    await this.loadPreview()
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize)
    window.removeEventListener("orientationchange", this.handleResize)
    window.removeEventListener("keydown", this.handleKeydown)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    getBrandImageUrl,
    onClose() {
      modalController.dismiss()
    },

    getPptOemBase() {
      let base = store.getOEMBASE() || ""
      if (base && /oem/i.test(base)) {
        base = base.replace(/\/oem(?=\/|$)/gi, "")
      }
      return base
    },

    /**
     * 课件地址：生产直连 CDN；开发把 resource.ayapple.com 改成同源路径走 vue 代理，避免 CORS
     */
    getPptUrl() {
      let url = this.getPptOemBase() + (this.fileHtml || "")
      if (process.env.NODE_ENV === "development" && url) {
        url = url.replace(/^https?:\/\/resource\.ayapple\.com/i, "")
        if (url && !url.startsWith("/") && !/^https?:\/\//i.test(url)) {
          url = `/${url}`
        }
      }
      return url
    },

    async loadPreview() {
      try {
        const fetchUrl = this.getPptUrl()
        console.log("[PptPreview] static fetch:", fetchUrl)
        const response = await fetch(fetchUrl)
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        const htmlContent = await response.text()
        this.buildIframePages(htmlContent)
      } catch (error) {
        console.error("加载 HTML 内容失败:", error)
        this.pages = [
          this.buildSrcdoc(
            `<p style="color:red;padding:16px;">加载失败: ${error.message}</p>`
          ),
        ]
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.setupResizeObserver()
          this.fitToScreen()
        })
      }
    },

    handleKeydown(event) {
      if (this.loading) return
      if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault()
        this.prevPage()
      } else if (event.key === "ArrowRight" || event.key === "PageDown" || event.key === " ") {
        event.preventDefault()
        this.nextPage()
      } else if (event.key === "Escape") {
        event.preventDefault()
        this.onClose()
      }
    },

    extractHeadAssets(doc) {
      const parts = []
      doc.querySelectorAll('link[rel="stylesheet"], style').forEach((el) => {
        parts.push(el.outerHTML)
      })
      return parts.join("\n")
    },

    buildSrcdoc(bodyHtml) {
      return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
${this.headAssets}
<style>${IFRAME_BASE_STYLE}</style>
</head>
<body>${bodyHtml}</body>
</html>`
    },

    buildIframePages(htmlContent) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(htmlContent, "text/html")
      this.headAssets = this.extractHeadAssets(doc)

      const slides = Array.from(doc.querySelectorAll(".slide"))
      if (slides.length > 0) {
        this.pages = slides.map((slide) => this.buildSrcdoc(slide.outerHTML))
        return
      }

      const bodyChildren = Array.from(doc.body?.children || []).filter((child) => {
        const text = child.textContent?.trim()
        return (
          (text && text.length > 0) ||
          child.children.length > 0 ||
          child.querySelectorAll("img, svg").length > 0
        )
      })

      if (bodyChildren.length > 0) {
        this.pages = bodyChildren.map((c) => this.buildSrcdoc(c.outerHTML))
      } else {
        this.pages = [this.buildSrcdoc(doc.body?.innerHTML || htmlContent)]
      }
    },

    onIframeLoad() {
      this.fitToScreen()
      this.$nextTick(() => {
        this.restoreDrawing(this.currentPageIndex)
      })
    },

    setupResizeObserver() {
      const wrapperEl = this.$refs.wrapperRef
      if (!wrapperEl || typeof ResizeObserver === "undefined") return

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.fitToScreen()
      })
      this.resizeObserver.observe(wrapperEl)
    },

    handleResize() {
      this.fitToScreen()
    },

    saveCurrentDrawing() {
      const board = this.$refs.drawingBoardRef
      if (!board) return
      this.pageDrawings[this.currentPageIndex] = board.toDataURL()
    },

    onDrawingClear() {
      this.pageDrawings[this.currentPageIndex] = ""
    },

    onDrawingChange() {
      this.saveCurrentDrawing()
    },

    restoreDrawing(pageIndex) {
      const board = this.$refs.drawingBoardRef
      if (!board) return
      board.loadDataURL(this.pageDrawings[pageIndex] || "")
    },

    prevPage() {
      if (this.currentPageIndex > 0) {
        this.saveCurrentDrawing()
        this.currentPageIndex--
        this.$nextTick(() => {
          this.fitToScreen()
        })
      }
    },

    nextPage() {
      if (this.currentPageIndex < this.pages.length - 1) {
        this.saveCurrentDrawing()
        this.currentPageIndex++
        this.$nextTick(() => {
          this.fitToScreen()
        })
      }
    },

    fitToScreen() {
      const wrapperEl = this.$refs.wrapperRef
      if (!wrapperEl) return

      const availableWidth = wrapperEl.clientWidth
      const availableHeight = wrapperEl.clientHeight
      if (availableWidth <= 0 || availableHeight <= 0) return

      const nextScale = Math.min(
        availableWidth / this.slideWidth,
        availableHeight / this.slideHeight
      )

      this.scale = Math.max(nextScale, 0.05)
      this.displayWidth = this.slideWidth * this.scale
      this.displayHeight = this.slideHeight * this.scale
    },
  },
}
</script>

<style scoped lang="less">
@import "@/assets/common.less";

.ppt-preview-page {
  width: 100%;
  height: 100%;
  background: #eef2f6;

  .toolbar {
    width: 100%;
    height: 52px;
    padding: 0 16px;
    box-sizing: border-box;
    background: #ffffff;
    border-bottom: 1px solid #e4e7ed;
    gap: 16px;

    .toolbar-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      height: 36px;
      padding: 0 14px;
      border: 1px solid #dcdfe6;
      border-radius: 8px;
      background: #f5f7fa;
      color: #303133;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      flex-shrink: 0;
      transition: background 0.15s ease;

      .nav-back {
        width: 7px;
        height: 12px;
        opacity: 0.85;
      }

      &:hover,
      &:active {
        background: #ebf0f5;
      }
    }

    .toolbar-title {
      flex: 1;
      min-width: 0;
      text-align: center;
      font-size: 16px;
      font-weight: 600;
      color: #303133;
      letter-spacing: 0.02em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .toolbar-meta {
      min-width: 88px;
      justify-content: flex-end;
      flex-shrink: 0;
    }

    .page-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 72px;
      height: 32px;
      padding: 0 12px;
      border-radius: 999px;
      background: rgba(43, 182, 115, 0.12);
      color: #2bb673;
      font-size: 14px;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
    }
  }
}

.stage-content {
  --background: #eef2f6;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 14px;
  color: #606266;
  font-size: 15px;
}

.stage {
  width: 100%;
  height: 100%;
  padding: 12px 8px;
  box-sizing: border-box;
  gap: 8px;
}

.page-side-btn {
  width: 56px;
  height: 96px;
  flex-shrink: 0;
  border: 1px solid #dcdfe6;
  border-radius: 12px;
  background: #ffffff;
  color: #303133;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: background 0.15s ease, transform 0.15s ease;

  ion-icon {
    font-size: 32px;
  }

  &:not(:disabled):hover {
    background: #f5f7fa;
  }

  &:not(:disabled):active {
    transform: scale(0.96);
    background: rgba(43, 182, 115, 0.12);
    color: #2bb673;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.slide-stage {
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.scale-viewport {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(15, 35, 52, 0.1);
}

.ppt-iframe {
  position: absolute;
  top: 0;
  left: 0;
  border: 0;
  transform-origin: 0 0;
  pointer-events: none;
  background: #fff;
}
</style>
